import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  authMethods,
  signInWithDevEmail,
  signInWithGoogle,
  signInWithPassword,
  signUpWithPassword,
  type AuthMethods,
} from '../services/auth';
import { apiClient } from '../services/api';
import { useSession } from '../store/session';

/**
 * Sign in, and sign up.
 *
 * The page asks the server which methods this deployment actually offers rather
 * than rendering every button it knows about. A Google button that 503s because
 * nobody configured the provider is worse than no Google button — the user
 * blames themselves.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useSession(state => state.setSession);

  const [methods, setMethods] = useState<AuthMethods | null>(null);
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void authMethods().then(setMethods);
  }, []);

  async function afterIdentity() {
    const session = await apiClient.session();
    setSession(session.profile, session.orgs);

    // A brand-new account has exactly one org — the personal workspace created
    // at signup — and no persona worth trusting yet, so it goes to onboarding.
    // Somebody who belongs to a school goes straight to the switcher.
    const hasRealOrg = session.orgs.some(org => org.kind !== 'personal');
    navigate(hasRealOrg ? '/switch' : '/onboarding', { replace: true });
  }

  /**
   * One click into a named demonstration identity.
   *
   * The accounts come from the server, which only offers them when it is
   * running development sign-in *and* has been given an explicit list. A
   * visitor should not have to be told an address out of band to look around,
   * and typing one in from a slide is how a demo goes quiet for thirty seconds.
   */
  async function enterAs(demoEmail: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    setEmail(demoEmail);

    try {
      await signInWithDevEmail(demoEmail);
      await afterIdentity();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);

    try {
      if (methods?.supabase) {
        if (mode === 'signup') {
          const { needsConfirmation } = await signUpWithPassword(email, password, fullName);
          if (needsConfirmation) {
            setNotice(`Check ${email} for a confirmation link, then sign in.`);
            setBusy(false);
            return;
          }
        } else {
          await signInWithPassword(email, password);
        }
      } else {
        await signInWithDevEmail(email, fullName || email);
      }
      await afterIdentity();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-6 px-4 py-10">
      <div className="text-center">
        <Link to="/" className="text-3xl" aria-label="STEM Africa Learning Hub">
          🔬
        </Link>
        <h1 className="mt-2 text-2xl font-bold text-brand-900">STEM Africa Learning Hub</h1>
        <p className="text-sm text-ink-600">
          Professional learning, teaching resources and virtual laboratories.
        </p>
      </div>

      <form className="card space-y-4" onSubmit={submit}>
        <div className="flex gap-2 rounded-lg bg-ink-100 p-1 text-sm">
          {(['signin', 'signup'] as const).map(option => (
            <button
              key={option}
              type="button"
              className={`flex-1 rounded-md px-3 py-1.5 font-medium ${
                mode === option ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-600'
              }`}
              onClick={() => setMode(option)}
            >
              {option === 'signin' ? 'Sign in' : 'Create account'}
            </button>
          ))}
        </div>

        {mode === 'signup' && (
          <div>
            <label className="label" htmlFor="fullName">
              Your name
            </label>
            <input
              id="fullName"
              className="input"
              value={fullName}
              onChange={event => setFullName(event.target.value)}
              autoComplete="name"
              required
            />
          </div>
        )}

        <div>
          <label className="label" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            className="input"
            type="email"
            inputMode="email"
            value={email}
            onChange={event => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
        </div>

        {methods?.supabase && (
          <div>
            <label className="label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className="input"
              type="password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              required
              minLength={8}
            />
          </div>
        )}

        {error && <p className="text-sm text-red-700">{error}</p>}
        {notice && <p className="text-sm text-emerald-700">{notice}</p>}

        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? 'Please wait…' : mode === 'signup' ? 'Create account' : 'Sign in'}
        </button>

        {methods?.supabase && (
          <button
            type="button"
            className="btn-secondary w-full"
            onClick={() => void signInWithGoogle().catch(caught => setError((caught as Error).message))}
          >
            Continue with Google
          </button>
        )}

        {methods && !methods.supabase && methods.devEmail && (
          <div className="space-y-2 rounded-lg bg-savanna-400/15 p-3 text-xs text-savanna-600">
            <p>
              This deployment is running <strong>development sign-in</strong>: an email address
              alone gets you in.
              {methods.demoAccounts.length > 0
                ? ' Only the demonstration accounts below are accepted.'
                : ' It is disabled in production.'}
            </p>

            {methods.demoAccounts.length > 0 && (
              <ul className="space-y-1">
                {methods.demoAccounts.map(account => (
                  <li key={account}>
                    <button
                      type="button"
                      className="w-full rounded-md bg-white/70 px-2.5 py-1.5 text-left font-mono text-[11px] text-ink-700 ring-1 ring-savanna-400/40 hover:bg-white disabled:opacity-60"
                      disabled={busy}
                      onClick={() => void enterAs(account)}
                    >
                      {account}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {methods && !methods.supabase && !methods.devEmail && (
          <p className="rounded-lg bg-red-50 p-3 text-xs text-red-700">
            No sign-in method is configured on this server. Set <code>SUPABASE_URL</code> or enable
            development sign-in.
          </p>
        )}
      </form>

      <p className="text-center text-xs text-ink-500">
        <Link to="/" className="underline">
          Back to the public site
        </Link>
      </p>
    </div>
  );
}
